/* Dart Hero 🛠️ — sections/custom-dart-hero.liquid
 * Hover (or tap on touch screens) plays a dart's rotation frames forward; leaving reverses them.
 * Below 768px the darts become a looping swipe carousel; the centred dart turns automatically. */

if (!customElements.get('dart-hero')) {
  const FPS = 55; // 23-frame turns take ~0.4s
  const CAROUSEL_STRIDE = 2; // the mobile carousel uses every other frame: half the download, same speed turn
  const canHover = matchMedia('(hover: hover)').matches;
  const MOBILE = '(max-width: 47.99em)'; // Align's tablet breakpoint (768px)
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const HINT_LOOPS = 3;      // times the swipe hint plays before fading out

  // The Lottie player is only needed for the mobile swipe hint, so load it on demand, once.
  let lottiePromise;
  const loadLottie = (src) => {
    lottiePromise ??= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(window.lottie);
      script.onerror = reject;
      document.head.append(script);
    });
    return lottiePromise;
  };

  // Frames are shared across darts (e.g. the demo set), so load each URL once. Each entry's `ready`
  // resolves once the frame is downloaded and decoded (or failed — draw() skips broken frames), so
  // the first turn doesn't stall decoding mid-animation.
  const imageCache = new Map();
  const loadImage = (src, priority = 'auto') => {
    if (!imageCache.has(src)) {
      const img = new Image();
      img.decoding = 'async';
      img.fetchPriority = priority;
      img.src = src;
      imageCache.set(src, { img, ready: img.decode().catch(() => {}) });
    }
    return imageCache.get(src);
  };

  class DartSpinner {
    constructor(el, hero) {
      this.el = el;
      this.hero = hero;
      this.canvas = el.querySelector('canvas');
      this.details = el.querySelector('.dart-hero__details');
      this.ctx = this.canvas.getContext('2d');
      this.urls = JSON.parse(el.dataset.frames || '[]');
      this.images = []; // by URL index
      this.setSequence();
      this.frame = 0;   // float position in the sequence
      this.drawn = -1;  // last integer frame painted
      this.target = 0;  // restFrame() or turnedFrame()
      this.active = false; // hovered / tapped / centred (a single-frame dart is active without rotating)
      this.last = 0;
      this.raf = null;

      // In carousel mode the carousel decides which dart is active, so hover and tap step aside.
      if (canHover) {
        el.addEventListener('pointerenter', () => { if (!hero.carousel) this.play(true); });
        el.addEventListener('pointerleave', () => { if (!hero.carousel) this.play(false); });
        // Clicking the dart itself goes to its product (skipped while the link is a # placeholder).
        this.canvas.addEventListener('click', () => {
          const href = el.dataset.href;
          if (!hero.carousel && href && href !== '#') window.location.href = href;
        });
      } else {
        // Touch: tapping a dart turns it and reverses any other active dart.
        el.addEventListener('click', () => {
          if (hero.carousel) return;
          const opening = !this.active;
          if (opening) {
            hero.spinners.forEach((s) => { if (s !== this && s.active) s.play(false); });
          }
          this.play(opening);
        });
      }
    }

    // The frames this mode plays, as URL indexes: all of them on desktop, every other one in the
    // carousel (always keeping the first and last). Frame positions below count within this list,
    // and the frame rate scales so a turn takes the same time either way.
    setSequence() {
      const last = Math.max(this.urls.length - 1, 0);
      const stride = this.hero.carousel ? CAROUSEL_STRIDE : 1;
      this.sequence = [];
      for (let i = 0; i < last; i += stride) this.sequence.push(i);
      this.sequence.push(last);
      this.lastFrame = this.sequence.length - 1;
      this.fps = last ? FPS * this.lastFrame / last : FPS;
      this.allReady = null; // the new sequence needs requesting
    }

    // Fetch one frame (by position in the sequence) and repaint when it arrives; draw() skips any
    // frame that hasn't loaded yet.
    fetchFrame(i, priority) {
      const u = this.sequence[i];
      this.images[u] ??= loadImage(this.urls[u], priority);
      return this.images[u].ready.then(() => this.draw());
    }

    // Desktop turns 0 → last. The mobile carousel runs it the other way, last → 0, so the centred
    // dart finishes on frame 0 with the shaft logo facing front.
    restFrame() { return this.hero.carousel ? this.lastFrame : 0; }
    turnedFrame() { return this.hero.carousel ? 0 : this.lastFrame; }

    // Jump (no animation) to the resting frame, e.g. when switching between desktop and carousel.
    setRest() {
      cancelAnimationFrame(this.raf);
      this.raf = null;
      this.active = false;
      this.el.classList.remove('is-active');
      this.setSequence();
      this.frame = this.target = this.restFrame();
      this.drawn = -1;
      if (this.hero.inView) this.load();
      this.draw();
    }

    // When the hero comes into view: just the resting frame, ahead of everything else.
    load() {
      this.fetchFrame(this.restFrame(), 'high');
      this.draw();
    }

    // First hover / tap / centring: the rest of the rotation. Resolves once every frame is ready.
    // The mobile carousel activates its centred dart straight away, so until the hero is in view
    // this just returns a promise that the in-view handler fulfils.
    loadAll() {
      if (!this.hero.inView) {
        this.pendingAll ??= new Promise((resolve) => { this.resolvePending = resolve; });
        return this.pendingAll;
      }
      if (!this.allReady) {
        const sequence = this.sequence;
        const frames = sequence.map((_, i) => this.fetchFrame(i));
        this.allReady = Promise.all(frames).then(() => { this.readySequence = sequence; });
        this.resolvePending?.(this.allReady);
        this.resolvePending = null;
      }
      return this.allReady;
    }

    play(forward) {
      this.active = forward;
      this.target = forward ? this.turnedFrame() : this.restFrame();
      this.el.classList.toggle('is-active', forward);
      cancelAnimationFrame(this.raf);
      this.raf = null;
      if (forward) {
        this.placeDetails();
        // Lift and show the details now, but only turn once the whole sequence is here: on a slow
        // connection a turn started early would sit still, then jump to the end.
        const ready = this.loadAll();
        if (this.readySequence !== this.sequence) {
          const sequence = this.sequence;
          ready.then(() => {
            if (this.active && this.sequence === sequence) this.start();
          });
          return;
        }
      }
      this.start();
    }

    start() {
      cancelAnimationFrame(this.raf);
      this.last = performance.now();
      this.raf = requestAnimationFrame((t) => this.tick(t));
    }

    // Show the box on the right unless it would run off the screen.
    placeDetails() {
      this.details.classList.remove('flip');
      if (this.hero.carousel) return; // box sits below the dart on mobile
      const r = this.details.getBoundingClientRect();
      this.details.classList.toggle('flip', r.right > document.documentElement.clientWidth - 16);
    }

    tick(now) {
      // Cap dt so a stalled tab doesn't skip the whole sequence in one jump.
      // rAF's timestamp can be slightly earlier than the performance.now() taken in play(),
      // so floor at 0 to avoid stepping backwards.
      const step = (Math.min(Math.max(now - this.last, 0), 100) / 1000) * this.fps;
      this.last = now;
      const dir = Math.sign(this.target - this.frame);
      this.frame += dir * step;
      // Clamp so we land exactly on the target and stop.
      if ((dir > 0 && this.frame >= this.target) || (dir < 0 && this.frame <= this.target)) {
        this.frame = this.target;
      }
      this.draw();
      this.raf = this.frame === this.target ? null : requestAnimationFrame((t) => this.tick(t));
    }

    draw() {
      const i = Math.min(this.lastFrame, Math.max(0, Math.round(this.frame)));
      if (i === this.drawn) return;
      const img = this.images[this.sequence[i]]?.img;
      if (!img || !img.complete || !img.naturalWidth) return; // not loaded yet — repaints on load
      if (this.canvas.width !== img.naturalWidth) {
        this.canvas.width = img.naturalWidth;
        this.canvas.height = img.naturalHeight;
      }
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.drawImage(img, 0, 0);
      this.drawn = i;
      this.canvas.classList.add('is-drawn'); // hides the CSS poster frame shown before the script runs
    }

    destroy() {
      cancelAnimationFrame(this.raf);
    }
  }

  // Mobile: one dart centred with its neighbours peeking in, swipe to move, loops endlessly.
  // Each dart is positioned by --rel (slides from centre) + --drag (finger offset) in the CSS.
  class DartCarousel {
    constructor(hero) {
      this.hero = hero;
      this.track = hero.querySelector('.dart-hero__darts');
      this.items = hero.spinners;
      this.n = this.items.length;
      this.index = 0;                    // which dart is centred
      this.rels = this.items.map(() => 0);
      this.drag = null;

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.track.addEventListener('pointerdown', this.onDown);
      this.track.addEventListener('pointermove', this.onMove);
      this.track.addEventListener('pointerup', this.onUp);
      this.track.addEventListener('pointercancel', this.onUp);

      hero.carousel = this; // set now so the darts know which way to turn while we activate below
      hero.classList.add('is-carousel');
      this.items.forEach((s) => s.setRest());
      this.layout(0, 0, true);
      this.activate();

      // Show the swipe hint once the carousel is mostly on screen.
      this.hint = hero.querySelector('.dart-hero__hint');
      this.hintObserver = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.hintObserver.disconnect();
        this.showHint();
      }, { threshold: 0.6 });
      this.hintObserver.observe(this.track);
    }

    // Play the "swipe" hand a few times, then fade it out. With reduced motion, show it still.
    async showHint() {
      if (!this.hint || this.touched || this.hintStarted) return;
      this.hintStarted = true;
      // The dart's own frames come first; the hint can wait.
      await this.items[this.index].loadAll();
      if (this.touched || !this.hero.carousel) return;
      let lottie;
      try { lottie = await loadLottie(this.hint.dataset.player); } catch { return; }
      if (this.touched || !this.hero.carousel) return; // interacted or left mobile while loading

      this.hintAnimation = lottie.loadAnimation({
        container: this.hint,
        renderer: 'svg',
        loop: !reduceMotion,
        autoplay: !reduceMotion,
        path: this.hint.dataset.src,
      });
      let loops = 0;
      this.hintAnimation.addEventListener('DOMLoaded', () => {
        if (reduceMotion) this.hintAnimation.goToAndStop(this.hintAnimation.totalFrames / 2, true);
        this.hint.classList.add('is-visible');
      });
      this.hintAnimation.addEventListener('loopComplete', () => {
        if (++loops >= HINT_LOOPS) this.hideHint();
      });
      if (reduceMotion) this.hintTimer = setTimeout(() => this.hideHint(), 3600);
    }

    // Fade the hint out (after its loops, or as soon as the user touches the carousel).
    hideHint() {
      this.touched = true; // once they've interacted, they don't need showing again
      clearTimeout(this.hintTimer);
      this.hintObserver?.disconnect();
      if (!this.hint) return;
      this.hint.classList.remove('is-visible');
      const animation = this.hintAnimation;
      this.hintAnimation = null;
      if (animation) setTimeout(() => animation.destroy(), 400); // after the fade
    }

    // Slides from centre for dart i. With 4 darts the one opposite the centre can sit either side;
    // put it on the side the user is dragging from so a neighbour is always waiting there.
    rel(i, dir) {
      let r = (((i - this.index) % this.n) + this.n) % this.n;
      if (r > this.n / 2 || (r === this.n / 2 && dir > 0)) r -= this.n;
      return r;
    }

    layout(dx, dir, instant = false) {
      this.items.forEach((s, i) => {
        const r = this.rel(i, dir);
        // A dart wrapping round from one side to the other must jump, not slide across the screen.
        const jump = Math.abs(r - this.rels[i]) > 1;
        s.el.style.transition = instant || jump || this.drag ? 'none' : '';
        s.el.style.setProperty('--rel', r);
        s.el.style.setProperty('--drag', `${dx}px`);
        this.rels[i] = r;
      });
    }

    // Centred dart turns and shows its details; every other dart reverses.
    activate() {
      this.items.forEach((s, i) => {
        const centred = i === this.index;
        if (centred && !s.active) s.play(true);
        else if (!centred && s.active) s.play(false);
      });
    }

    go(step, dir) {
      this.index = (((this.index + step) % this.n) + this.n) % this.n;
      this.layout(0, dir);
      this.activate();
    }

    onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('a')) return; // let links in the details box work normally
      this.hideHint();
      this.drag = { id: e.pointerId, x: e.clientX, dx: 0, t: performance.now() };
      try { this.track.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
    }

    onMove(e) {
      if (!this.drag || e.pointerId !== this.drag.id) return;
      this.drag.dx = e.clientX - this.drag.x;
      this.layout(this.drag.dx, Math.sign(this.drag.dx));
    }

    onUp(e) {
      if (!this.drag || e.pointerId !== this.drag.id) return;
      const { dx, t } = this.drag;
      const velocity = dx / Math.max(performance.now() - t, 1); // px per ms
      this.drag = null;

      // A tap on a peeking dart brings it to the centre.
      if (Math.abs(dx) < 6) {
        const i = this.items.findIndex((s) => s.el.contains(e.target));
        if (i >= 0 && this.rels[i] !== 0) {
          this.go(this.rels[i], -this.rels[i]);
        } else {
          this.layout(0, 0);
        }
        return;
      }

      // Far enough, or a quick flick, moves one dart; otherwise spring back.
      const slide = this.items[0].el.offsetWidth;
      const flick = Math.abs(velocity) > 0.4 && Math.abs(dx) > 24;
      if (Math.abs(dx) > slide * 0.2 || flick) this.go(dx < 0 ? 1 : -1, Math.sign(dx));
      else this.layout(0, Math.sign(dx));
    }

    destroy() {
      this.track.removeEventListener('pointerdown', this.onDown);
      this.track.removeEventListener('pointermove', this.onMove);
      this.track.removeEventListener('pointerup', this.onUp);
      this.track.removeEventListener('pointercancel', this.onUp);
      this.hideHint();
      this.hero.classList.remove('is-carousel');
      this.items.forEach((s) => {
        s.el.style.removeProperty('transition');
        s.el.style.removeProperty('--rel');
        s.el.style.removeProperty('--drag');
        s.setRest(); // hero.carousel is already cleared, so this is the desktop resting frame
      });
    }
  }

  class DartHero extends HTMLElement {
    // connected/disconnected also run when the theme editor re-renders the section.
    connectedCallback() {
      this.spinners = [...this.querySelectorAll('.dart-hero__dart')].map((el) => new DartSpinner(el, this));
      // Only download frames once the hero is near the viewport.
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.inView = true;
        // Every resting frame first, then the rotation for any dart that's already active.
        this.spinners.forEach((s) => s.load());
        this.spinners.forEach((s) => { if (s.pendingAll) s.loadAll(); });
      }, { rootMargin: '300px' });
      this.observer.observe(this);

      // Switch between the 4-up row and the carousel as the screen crosses 768px.
      this.mobile = matchMedia(MOBILE);
      this.onMedia = () => this.updateMode();
      this.mobile.addEventListener('change', this.onMedia);
      this.updateMode();
    }

    updateMode() {
      if (this.mobile.matches && !this.carousel) {
        this.carousel = new DartCarousel(this);
      } else if (!this.mobile.matches && this.carousel) {
        const carousel = this.carousel;
        this.carousel = null; // clear first so the darts reset to the desktop resting frame
        carousel.destroy();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.mobile?.removeEventListener('change', this.onMedia);
      const carousel = this.carousel;
      this.carousel = null;
      carousel?.destroy();
      this.spinners?.forEach((s) => s.destroy());
    }
  }

  customElements.define('dart-hero', DartHero);
}
